import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, ShieldAlert, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLE_LABELS = { super_admin: "Super Admin", manager: "Manager", staff: "Staff" };
const ROLE_DESC = {
  super_admin: "Full access, including managing admin users & settings.",
  manager: "Everything except managing admin users.",
  staff: "Products, categories, enquiries and orders only.",
};
const roleBadge = {
  super_admin: "bg-brand-gold text-black",
  manager: "bg-blue-100 text-blue-700",
  staff: "bg-slate-100 text-slate-600",
};
const empty = { username: "", password: "", name: "", role: "staff", active: true };

export default function AdminUsers() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const isSuper = me?.role === "super_admin";

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => api.get("/admin/users").then((r) => r.data),
    enabled: isSuper,
  });

  if (!isSuper) {
    return (
      <div data-testid="admin-users-denied" className="max-w-lg mx-auto mt-16 text-center bg-white border rounded-xl p-10">
        <ShieldAlert className="w-12 h-12 mx-auto text-red-500 mb-4" />
        <h1 className="font-display font-bold text-2xl text-slate-900">Restricted area</h1>
        <p className="text-slate-500 mt-2">Only a Super Admin can manage admin users.</p>
      </div>
    );
  }

  const openNew = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ username: u.username, password: "", name: u.name || "", role: u.role, active: u.active }); setDialogOpen(true); };

  const save = async () => {
    if (!editing && (!form.username || !form.password)) { toast.error("Username and password are required."); return; }
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, active: form.active };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/users/${editing.id}`, payload);
        toast.success("Admin user updated");
      } else {
        await api.post("/admin/users", form);
        toast.success("Admin user created");
      }
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      setDialogOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/users/${deleteId}`);
      toast.success("Admin user deleted");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setDeleteId(null); }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="font-display font-black text-3xl text-slate-900">Admin Users</h1>
          <p className="text-slate-500">{users.length} account(s)</p>
        </div>
        <button onClick={openNew} data-testid="add-user-btn" className="inline-flex items-center gap-2 bg-brand-blue text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-brand-blue/90">
          <Plus className="w-4 h-4" /> Add admin
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        {Object.keys(ROLE_LABELS).map((r) => (
          <div key={r} className="bg-white border rounded-xl p-4">
            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${roleBadge[r]}`}><ShieldCheck className="w-3.5 h-3.5" /> {ROLE_LABELS[r]}</span>
            <p className="text-sm text-slate-500 mt-2">{ROLE_DESC[r]}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">Username</th>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.username}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">
                      {u.username}{u.id === me.id && <span className="ml-2 text-xs text-slate-400">(you)</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{u.name || "—"}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${roleBadge[u.role]}`}>{ROLE_LABELS[u.role]}</span></td>
                    <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${u.active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{u.active ? "Active" : "Disabled"}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.username}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"><Pencil className="w-4 h-4" /></button>
                        {u.id !== me.id && (
                          <button onClick={() => setDeleteId(u.id)} data-testid={`delete-user-${u.username}`} className="p-2 rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.username}` : "Add admin user"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1">Username {editing && <span className="text-slate-400 font-normal">(cannot be changed)</span>}</label>
              <input data-testid="user-form-username" value={form.username} disabled={!!editing} onChange={(e) => setForm({ ...form, username: e.target.value })} className="w-full px-3 py-2 border rounded-lg disabled:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Full name</label>
              <input data-testid="user-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">{editing ? "New password (leave blank to keep)" : "Password"}</label>
              <input data-testid="user-form-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Role</label>
              <select data-testid="user-form-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} disabled={editing && editing.id === me.id} className="w-full px-3 py-2 border rounded-lg bg-white disabled:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-blue/30">
                <option value="staff">Staff</option>
                <option value="manager">Manager</option>
                <option value="super_admin">Super Admin</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">{ROLE_DESC[form.role]}</p>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input data-testid="user-form-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4" /> Active
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="px-4 py-2 rounded-lg border font-medium">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-user-btn" className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-brand-blue text-white font-semibold disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete admin user?</AlertDialogTitle>
            <AlertDialogDescription>They will immediately lose access. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-user" className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
